import React from 'react';
import { Customized, RadialBar, RadialBarChart, ResponsiveContainer, Legend } from 'recharts';
import { ChartTooltip, ChartContainer, ChartConfig } from '@/components/ui/chart';
import CustomTooltip from './CustomTooltip';
export interface RadialData {
  name: string;
  value: number;
  color: string;
}

interface RadialProps {
  data: RadialData[];
  width?: string;
  height?: string;
  innerRadius?: number;
  outerRadius?: number;
  cx?: string;
  cy?: string;
  startAngle?: number;
  endAngle?: number;
  showLegend?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  dataKey?: string;
  nameKey?: string;
  showTooltip?: boolean;
  showBackground?: boolean;
  chartTooltipContent?: any;
  legendClassName?: string;
  textX?: string;
  textY?: string;
  children?: React.ReactNode;
  customContent?: (props: any) => React.ReactElement;
  wrapperLegendStyle?: React.CSSProperties;
  legendVerticalAlign?: 'top' | 'bottom' | 'middle';
  legendAlign?: 'left' | 'right' | 'center';
  legendLayout?: 'vertical' | 'horizontal';
  wrapperClassName?: string;
}

const RadialChartComponent: React.FC<RadialProps> = ({
  data,
  width = '100%',
  height = '100%',
  innerRadius = '10%',
  outerRadius = '80%',
  cx = '50%',
  cy = '50%',
  startAngle = 90,
  endAngle = 450,
  legendPosition = 'right',
  dataKey = 'value',
  nameKey = 'label',
  showTooltip = true,
  showBackground = true,
  children,
  customContent,
  wrapperLegendStyle,
  legendVerticalAlign = 'bottom',
  legendAlign = 'center',
  legendLayout = 'vertical',
  wrapperClassName
}) => {
  // 动态生成 chartConfig
  const chartConfig: ChartConfig = {
    ...data.reduce((config, item) => {
      config[item.name] = {
        label: item.name.charAt(0).toUpperCase() + item.name.slice(1),
        color: item.color,
        value: item.name.charAt(0).toUpperCase() + item.name.slice(1)
      };
      return config;
    }, {} as Record<string, { label: string; color: string; value: string }>)
  };

  // 转换数据格式以适配 RadialBarChart
  const chartData = data.map(item => ({
    [nameKey]: item.name,
    name: item.name,
    [dataKey]: item.value,
    fill: item.color
  }));

  return (
    <div
      className={`relative flex h-full w-full items-center gap-4 ${
        legendPosition === 'bottom' ? 'flex-col' : ''
      } ${wrapperClassName}`}
    >
      <div style={{ width, height }}>
        <ResponsiveContainer>
          <ChartContainer
            config={chartConfig}
            className={`aspect-square ${legendPosition === 'right' ? 'flex-1' : ''}`}
            style={{ maxHeight: height }}
          >
            <RadialBarChart
              data={chartData}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              startAngle={startAngle}
              endAngle={endAngle}
              cx={cx}
              cy={cy}
            >
              {showTooltip && <ChartTooltip cursor={false} content={CustomTooltip} />}
              <Legend
                verticalAlign={legendVerticalAlign}
                align={legendAlign}
                layout={legendLayout}
                iconSize={8}
                iconType="square"
                wrapperStyle={wrapperLegendStyle}
              />
              <RadialBar dataKey={dataKey} background={showBackground} />
              {/* 自定义内容插槽 */}
              {customContent && <Customized component={customContent} />}
              {/* 子组件插槽 */}
              {children}
            </RadialBarChart>
          </ChartContainer>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RadialChartComponent;
